import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../../../config/api";
import { formatDate } from "../../../config/utils";
import EmptyState from "../../../components/EmptyState";
import Loader from "../../../components/Loader";
import PageHeader from "../../../components/PageHeader";
import { FinanceBadge, formatMoney } from "../financeUtils";

const EMPTY_SUMMARY = {
  outstandingInvoices: 0,
  paidThisMonth: 0,
  pendingExpenseAmount: 0,
  pendingExpenses: 0,
  pendingAdminRequests: 0,
  overdueInvoices: 0,
  budgetAllocated: 0,
  budgetSpent: 0,
};

function SummaryMetric({ label, value, tone, money }) {
  const accent =
    tone === "success" ? "var(--success)" :
    tone === "danger" ? "var(--danger)" :
    tone === "warn" ? "var(--warn)" :
    "#d97706";

  return (
    <div className="summary-card" style={{ borderTop: `3px solid ${accent}` }}>
      <div className="summary-card-label">{label}</div>
      <div className="summary-card-value" style={{ color: accent }}>
        {money ? formatMoney(value) : value}
      </div>
    </div>
  );
}

export default function FinanceDashboardPage() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [invoices, setInvoices] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [summaryRes, invoicesRes, expensesRes, requestsRes] = await Promise.all([
        apiFetch("/api/finance/summary"),
        apiFetch("/api/finance/invoices"),
        apiFetch("/api/finance/expenses?status=Pending"),
        apiFetch("/api/finance/admin-requests?status=Pending"),
      ]);

      if (!summaryRes.ok) throw new Error(await summaryRes.text());
      if (!invoicesRes.ok) throw new Error(await invoicesRes.text());
      if (!expensesRes.ok) throw new Error(await expensesRes.text());
      if (!requestsRes.ok) throw new Error(await requestsRes.text());

      setSummary(await summaryRes.json());
      setInvoices(await invoicesRes.json());
      setExpenses(await expensesRes.json());
      setRequests(await requestsRes.json());
    } catch (err) {
      setError(err?.message || "Could not load finance dashboard.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const budgetUsedPct = summary.budgetAllocated > 0
    ? Math.min(100, Math.round((summary.budgetSpent / summary.budgetAllocated) * 100))
    : 0;

  return (
    <div className="finance-page">
      <PageHeader
        title="Finance Dashboard"
        subtitle="Invoices, expenses, budgets, vendors, and admin approvals in one view."
        actions={
          <button className="primary" onClick={() => navigate("/finance/invoices")}>
            Create Invoice
          </button>
        }
      />

      {error && <div className="error-banner" style={{ marginBottom: 16 }}>{error}</div>}

      {loading ? (
        <Loader.SummaryCards />
      ) : (
        <div className="summary-grid">
          <SummaryMetric label="Outstanding" value={summary.outstandingInvoices} money />
          <SummaryMetric label="Paid This Month" value={summary.paidThisMonth} money tone="success" />
          <SummaryMetric label="Pending Expenses" value={summary.pendingExpenses} tone="warn" />
          <SummaryMetric label="Pending Amount" value={summary.pendingExpenseAmount} money tone="warn" />
          <SummaryMetric label="Admin Approvals" value={summary.pendingAdminRequests} tone="danger" />
          <SummaryMetric label="Overdue Invoices" value={summary.overdueInvoices} tone="danger" />
        </div>
      )}

      <div className="finance-dashboard-grid">
        <section className="glass-card finance-panel">
          <div className="finance-panel-header">
            <div>
              <h3>Budget Utilization</h3>
              <p>{formatMoney(summary.budgetSpent)} used of {formatMoney(summary.budgetAllocated)}</p>
            </div>
            <span className="finance-percent">{budgetUsedPct}%</span>
          </div>
          <div className="finance-progress-wrap">
            <div className="finance-progress">
              <span style={{ width: `${budgetUsedPct}%` }} />
            </div>
          </div>
        </section>

        <section className="glass-card finance-panel">
          <div className="finance-panel-header">
            <div>
              <h3>Recent Invoices</h3>
              <p>Latest client billing records.</p>
            </div>
            <button className="btn-ghost" onClick={() => navigate("/finance/invoices")}>View All</button>
          </div>

          {loading ? (
            <div className="finance-list-skeleton">
              {[1, 2, 3].map((item) => <Loader key={item} type="skeleton" height={42} />)}
            </div>
          ) : invoices.length === 0 ? (
            <EmptyState title="No invoices yet" subtitle="Create invoices from CRM wins or project milestones." />
          ) : (
            <div className="finance-compact-list">
              {invoices.slice(0, 5).map((invoice) => (
                <div key={invoice.id} className="finance-compact-row">
                  <div>
                    <div className="finance-row-title">{invoice.clientName}</div>
                    <div className="finance-row-subtitle">{invoice.invoiceNumber} - due {formatDate(invoice.dueDate)}</div>
                  </div>
                  <div className="finance-row-right">
                    <strong>{formatMoney(invoice.totalAmount)}</strong>
                    <FinanceBadge status={invoice.status} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="glass-card finance-panel">
          <div className="finance-panel-header">
            <div>
              <h3>Pending Expenses</h3>
              <p>Expense approvals waiting for review.</p>
            </div>
            <button className="btn-ghost" onClick={() => navigate("/finance/expenses")}>Review</button>
          </div>
          {loading ? (
            <div className="finance-list-skeleton">
              {[1, 2, 3].map((item) => <Loader key={item} type="skeleton" height={42} />)}
            </div>
          ) : expenses.length === 0 ? (
            <EmptyState title="No pending expenses" subtitle="Approved and paid expenses stay on the Expenses page." />
          ) : (
            <div className="finance-compact-list">
              {expenses.slice(0, 5).map((expense) => (
                <div key={expense.id} className="finance-compact-row">
                  <div>
                    <div className="finance-row-title">{expense.title}</div>
                    <div className="finance-row-subtitle">{expense.category} - {expense.requestedByUserName || "User"}</div>
                  </div>
                  <div className="finance-row-right">
                    <strong>{formatMoney(expense.amount)}</strong>
                    <FinanceBadge status={expense.status} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="glass-card finance-panel">
          <div className="finance-panel-header">
            <div>
              <h3>Admin Approvals</h3>
              <p>Purchases and admin requests needing decisions.</p>
            </div>
            <button className="btn-ghost" onClick={() => navigate("/finance/approvals")}>Open</button>
          </div>
          {loading ? (
            <div className="finance-list-skeleton">
              {[1, 2, 3].map((item) => <Loader key={item} type="skeleton" height={42} />)}
            </div>
          ) : requests.length === 0 ? (
            <EmptyState title="No admin approvals" subtitle="New admin requests will appear here." />
          ) : (
            <div className="finance-compact-list">
              {requests.slice(0, 5).map((request) => (
                <div key={request.id} className="finance-compact-row">
                  <div>
                    <div className="finance-row-title">{request.title}</div>
                    <div className="finance-row-subtitle">{request.requestType} - {request.department || "General"}</div>
                  </div>
                  <div className="finance-row-right">
                    <strong>{formatMoney(request.amount)}</strong>
                    <FinanceBadge status={request.status} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
